import type { APIError } from '@vizit/api';
import { toRetailerKey } from '@vizit/constants';
import { ScorePDPDialog, ScorePDPErrorType } from '@vizit/organisms';
import { useRetailerRegion } from '../../hooks/use-retailer-region';
import { isRegion, isRetailer } from '../../utils/retailer-region-guards';
import { ProductImportErrorType, useProductImportDialog } from './product-import-dialog.hooks';

interface ProductImportDialogProps {
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Callback to notify when the dialog open state changes
   * @param open - Whether the dialog should be open
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  workspaceId?: string;
  useIcon?: boolean;
  onSuccessfulImport: (pdpId: string) => void;
  onImportError?: (error: unknown | APIError) => void;
}

const ERROR_TYPE_MAP: Record<ProductImportErrorType, ScorePDPErrorType> = {
  [ProductImportErrorType.None]: ScorePDPErrorType.None,
  [ProductImportErrorType.Duplicate]: ScorePDPErrorType.Duplicate,
  [ProductImportErrorType.UnsupportedCategory]: ScorePDPErrorType.UnsupportedCategory,
  [ProductImportErrorType.ClassificationFailed]: ScorePDPErrorType.ClassificationFailed,
  [ProductImportErrorType.Generic]: ScorePDPErrorType.Generic,
};

function ProductImportDialog(props: ProductImportDialogProps) {
  const {
    isOpen,
    onOpenChange,
    hideTrigger = false,
    workspaceId,
    useIcon = false,
    onSuccessfulImport,
    onImportError,
  } = props;

  const { data: retailerRegionData, isLoading: isRetailerRegionLoading } = useRetailerRegion();
  const retailer = retailerRegionData?.retailer;
  const region = retailerRegionData?.region;
  const orgRetailerKey = toRetailerKey(retailerRegionData?.id);

  const {
    errorType,
    duplicatePdpId,
    onSubmit: onPDPSubmit,
    resetState,
  } = useProductImportDialog({
    workspaceId,
    retailer: retailer && isRetailer(retailer) ? retailer : undefined,
    region: region && isRegion(region) ? region : undefined,
    onSuccessfulImport,
    onImportError,
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetState();
    }
    onOpenChange?.(open);
  };

  const handleOpenDuplicate = () => {
    if (duplicatePdpId) {
      onSuccessfulImport(duplicatePdpId);
    }
  };

  return (
    <ScorePDPDialog
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      hideTrigger={hideTrigger}
      errorType={ERROR_TYPE_MAP[errorType]}
      useIcon={useIcon}
      isLoading={isRetailerRegionLoading}
      orgRetailerKey={orgRetailerKey}
      onSubmit={onPDPSubmit}
      onOpenDuplicate={duplicatePdpId ? handleOpenDuplicate : undefined}
    />
  );
}

export { ProductImportDialog, ERROR_TYPE_MAP as PRODUCT_IMPORT_ERROR_TYPE_MAP };
